import React, { memo, useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { usePageSchema } from 'hooks/usePageSchema';
import { getCurrenciesPairInfo } from 'selectors';
import { getCurrencyCase } from 'utils';

import Page from 'components/Page';
import PageHelmet from 'components/PageHelmet';
import PageInner from 'components/PageInner';
import ConvertorPageContent from 'components/ConvertorPageContent ';
import PageSidebar from 'components/PageSidebar';
import LayoutContainer from 'components/LayoutContainer';

import NbuPageFooter from 'components/NbuPageFooter';

import { Content, Wrapper } from './styles';

const ConvertorPage = memo(({ location, match }) => {
  const intl = useIntl();

  const { source, target } = useSelector(state =>
    getCurrenciesPairInfo(state, { match })
  );

  const defaultMetaTitle = useMemo(() => {
    if (!source?.code || !target?.code) {
      return intl.formatMessage({
        id: 'homePage.metaTitle',
      });
    }

    const { currencyGS } = getCurrencyCase(source, target, 'g');
    const { currencyMnpsT } = getCurrencyCase(source, target, 'mnps');

    return intl.formatMessage(
      {
        id: 'converterPage.metaTitle',
      },
      {
        currencyGS,
        currencyMnpsT,
      }
    );
  }, [intl, source, target]);

  const defaultMetaDescription = useMemo(() => {
    if (!source?.code || !target?.code) {
      return intl.formatMessage({
        id: 'homePage.metaDescription',
      });
    }

    const { currencyGS } = getCurrencyCase(source, target, 'g');
    const { currencyMnpsT } = getCurrencyCase(source, target, 'mnps');

    return intl.formatMessage(
      {
        id: 'converterPage.metaDescription',
      },
      {
        currencyGS,
        currencyMnpsT,
        currencyCodeS: source.code,
        currencyCodeT: target.code,
      }
    );
  }, [intl, source, target]);

  const {
    pageScripts,
    formattedMetaTitle,
    formattedMetaDescription,
  } = usePageSchema({
    location,
    defaultMetaTitle,
    defaultMetaDescription,
  });

  return (
    <Page>
      <Wrapper>
        <LayoutContainer>
          <PageHelmet
            title={formattedMetaTitle}
            description={formattedMetaDescription}
            script={pageScripts}
          />
        </LayoutContainer>
      </Wrapper>
      <Content>
        <LayoutContainer>
          <PageInner>
            <ConvertorPageContent />
            <PageSidebar isInfoContentDisplayed={false} />
            <NbuPageFooter />
          </PageInner>
        </LayoutContainer>
      </Content>
    </Page>
  );
});

ConvertorPage.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      sourceCurrency: PropTypes.string,
      targetCurrency: PropTypes.string,
    }),
  }),
};

ConvertorPage.defaultProps = {
  match: null,
};

export default withRouter(ConvertorPage);
